import { events } from "@constants/tracking";
import { urls } from "@constants/urls";
import { useThemeContext } from "@contexts/ThemeContext";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { formatDateToLocalizedString } from "@helpers/date";
import {
	buildType,
	currentAppVersion,
	versionCode,
} from "@helpers/expo-extras";
import i18n from "@helpers/lib/i18n";
import { isWeb } from "@helpers/platform";
import { getPurchasedProducts } from "@helpers/user";
import useAnalytics from "@hooks/useAnalytics";
import { useAppStore } from "@hooks/useAppStore";
import useClerkUser from "@hooks/useClerkUser";
import useExams from "@hooks/useExams";
import { getPlatform } from "@memorang/helpers/src/common";
import { composeMessage } from "@memorang/helpers/src/email";
import { useRouter } from "expo-router";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
/* -----------------Third parties--------------- */
import { Linking } from "react-native";
/* -----------------Helpers && Hooks--------------- */
import type { Section } from "../types";

const isDevelopment = process.env.NODE_ENV === "development";

type Callbacks = {
	toggleShowDatePicker: () => void;
	toggleShowSignOutDialog: () => void;
	toggleDarkMode: () => void;
	toggleShowDeleteFeedbackDialog: () => void;
	toggleLanguageDialog: () => void;
	toggleShowPurchaseDialog: () => void;
	toggleShowFaqsDialog: () => void;
	toggleAudioPlaybackTestDialog: () => void;
	toggleShowAppSelectionDialog: () => void;
};
const useAccountSettings = ({
	toggleShowDatePicker,
	toggleShowSignOutDialog,
	toggleDarkMode,
	toggleShowDeleteFeedbackDialog,
	toggleLanguageDialog,
	toggleShowPurchaseDialog,
	toggleShowFaqsDialog,
	toggleAudioPlaybackTestDialog,
	toggleShowAppSelectionDialog,
}: Callbacks) => {
	const router = useRouter();
	const { t } = useTranslation();

	const {
		currentExamContext: { examDate, examName, examIcon },
	} = useExamContext();

	const { email } = useClerkUser();

	const { explicitCallTrackCustomEvent } = useAnalytics();

	const { newUserInfo } = useAuthContext();

	const username = newUserInfo?.name;

	const { isDarkMode } = useThemeContext();

	const exam = useAppStore((store) => store.exam);
	const supportEmail = useAppStore((store) => store.supportEmail);

	const privacyLink = useAppStore((store) => store.app.links.privacy);
	const showLanguageSettings = useAppStore(
		(store) => store.app.accountSettings?.showLanguageSettings,
	);
	const showPurchasedProducts = useAppStore(
		(store) => store.app.accountSettings?.showPurchasedProducts,
	);

	const showFaqs = useAppStore((store) => store.app.accountSettings?.showFaqs);

	const showPartnersSection = useAppStore(
		(store) => store.app.accountSettings?.showPartnersSection,
	);

	const showAudioTestDialog = useAppStore(
		(store) => store.app.accountSettings?.showAudioTestDialog,
	);

	const allowChangeExam = useAppStore((store) => store.exam.allowChangeExam);
	const tosLink = useAppStore((store) => store.app.links.tos);
	const { examsList } = useExams();

	const purchasedProducts = getPurchasedProducts(examsList);

	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
	const getAccountSections = (): Section[] => {
		const currentLanguage = (() => {
			switch (i18n.language) {
				case "en-US":
					return t("language.english");
				case "zh-CN":
					return t("language.chinese");
				case "ja-JP":
					return t("language.japanese");
				default:
					return t("language.english");
			}
		})();

		return [
			...(exam?.enabled
				? [
						...(allowChangeExam
							? [
									{
										sectionTitle: t("account.currentExam"),
										items: [
											{
												icon: examIcon || "file-document-outline",
												type: "exam-filter",
												title: examName,
												iconRight: "swap-horizontal",
											},
										],
									},
								]
							: []),
						...(purchasedProducts.length > 0 && showPurchasedProducts
							? [
									{
										sectionTitle: t("account.currentExam"),
										items: purchasedProducts.map(
											({
												bundle: {
													name,
													icon: { url },
												},
												metadata,
											}) => {
												return {
													icon: url,
													type: "extend-subscription",
													title: name,
													description: `${t("account.validUntil", {
														date: DateTime.fromISO(
															metadata.subscription.endDate,
														).toLocaleString(DateTime.DATE_FULL),
													})}`,
													onPress: isWeb
														? toggleShowPurchaseDialog
														: () => {
																router.push({
																	pathname: "/purchase",
																	params: {
																		productType: "STUDY_PACK",
																	},
																});
															},
												};
											},
										),
									},
								]
							: []),
						{
							sectionTitle: t("account.examDate"),
							items: [
								{
									icon: "calendar-outline",
									type: "datepicker",
									title: examDate
										? formatDateToLocalizedString(examDate)
										: t("account.selectDate"),
									onPress: toggleShowDatePicker,
									iconRight: "pencil-outline",
								},
							],
						},
						{
							sectionTitle: t("account.appearance"),
							items: [
								{
									icon: isDarkMode ? "weather-sunny" : "weather-night",
									title: t("account.darkMode"),
									type: "toggle",
									onPress: toggleDarkMode,
								},
							],
						},
						...(showAudioTestDialog
							? [
									{
										sectionTitle: t("account.features"),
										items: [
											{
												icon: "volume-high",
												title: t("account.audioPlaybackTest"),
												type: "toggle-audio-playback",
												onPress: toggleAudioPlaybackTestDialog,
											},
										],
									},
								]
							: []),
						{
							sectionTitle: t("account.accountInfo"),
							items: [
								{
									icon: "account-outline",
									title: t("account.name"),
									description: username || "",
								},
								{
									icon: "email-outline",
									title: t("account.email"),
									description: email,
								},
							],
						},
					]
				: []),
			...(showLanguageSettings
				? [
						{
							sectionTitle: t("language.display"),
							items: [
								{
									icon: "translate",
									title: t("language.sectionTitle"),
									onPress: toggleLanguageDialog,
									description: t("language.currentLanguage", {
										langname: currentLanguage,
									}),
								},
							],
						},
					]
				: []),
			...(isDevelopment
				? [
						{
							sectionTitle: t("account.dev"),
							items: [
								{
									onPress: () => {
										router.push("/sandbox");
									},
									icon: "code-json",
									title: t("account.sandbox"),
								},
								{
									onPress: toggleShowAppSelectionDialog,
									icon: "apps",
									title: "Switch app",
									description: `Current app: ${buildType}`,
								},
							],
						},
					]
				: []),
			{
				sectionTitle: t("account.about"),
				items: [
					{
						onPress: () => {
							explicitCallTrackCustomEvent({
								eventName: events.termsOfServiceClicked,
							});
							Linking.openURL(tosLink);
						},
						icon: "gavel",
						title: t("account.termsOfService"),
						iconRight: "open-in-new",
					},
					{
						onPress: () => {
							explicitCallTrackCustomEvent({
								eventName: events.privacyPolicyClicked,
							});
							Linking.openURL(privacyLink);
						},
						icon: "eye-outline",
						title: t("account.privacyPolicy"),
						iconRight: "open-in-new",
					},
					{
						onPress: () => {
							explicitCallTrackCustomEvent({
								eventName: events.contactSupportClicked,
								username,
								email,
							});
							const platform = getPlatform();
							const subject = `${buildType} ${t("account.support")} | ${platform} | ${username}`;
							const body = t("account.supportEmailBody", { username, email });
							composeMessage(subject, body, supportEmail);
						},
						icon: "help-circle-outline",
						title: t("account.contactSupport"),
						iconRight: "open-in-new",
					},
					...(showFaqs
						? [
								{
									onPress: isWeb
										? toggleShowFaqsDialog
										: () => {
												router.push("/faqs");
											},
									icon: "message-question-outline",
									title: t("account.frequentlyAskedQuestions"),
								},
							]
						: []),
					{
						onPress: toggleShowSignOutDialog,
						icon: "logout",
						title: t("account.signOut"),
					},
					{
						onPress: toggleShowDeleteFeedbackDialog,
						icon: "account-remove-outline",
						title: t("account.deleteAccount"),
					},
				],
			},
			{
				sectionTitle: t("account.platform"),
				items: [
					...(showPartnersSection
						? [
								{
									onPress: () => {
										Linking.openURL(urls.core);
									},
									icon: "web",
									title: t("account.partnerships"),
									iconRight: "open-in-new",
								},
							]
						: []),
					{
						icon: "unfold-more-vertical",
						title: `${currentAppVersion}${versionCode ? ` (${versionCode})` : ""}`,
					},
				],
			},
		];
	};
	return {
		isLoading: false,
		getAccountSections,
		usernameValue: username,
		actualUsernameValue: username,
	};
};

export default useAccountSettings;
